import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  DreamerExperimentArtifact,
  FlowPostmortem,
  ReasoningEffort,
} from "~/api/reasoningTypes";
import type { Run } from "~/api/types";
import { Button } from "~/foundation/components/Button";
import { ExperimentSection } from "./ExperimentSection";
import type { ActionSpec } from "./usePendingAction";

/** The run's postmortem: submitted to the Dreamer inbox, processed by a local Dreamer, tested as an experiment. */
export function PostmortemSection({
  run,
  postmortem,
  experiments,
  thinkerId,
  effort,
  effortMode,
  busy,
  act,
}: {
  run: Run;
  postmortem: FlowPostmortem | null;
  experiments: DreamerExperimentArtifact[];
  /** The selected local agent, when there is one. */
  thinkerId: string | undefined;
  effort: ReasoningEffort;
  effortMode: "budgeted" | "native";
  busy: boolean;
  act: (action: ActionSpec) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState("");
  const [outcome, setOutcome] = useState("");
  const [lessons, setLessons] = useState("");
  const [acknowledgedAttemptId, setAcknowledgedAttemptId] = useState<string | null>(null);
  const acknowledgeUncertain =
    postmortem?.dreamerAttemptId != null && acknowledgedAttemptId === postmortem.dreamerAttemptId;
  return (
    <section className="flex flex-col gap-2 border-t border-divider pt-4">
      <h4 className="font-semibold">{t("Reasoning postmortem → Dreamer")}</h4>
      {postmortem ? (
        <>
          <p>{t("Status: {{status}}", { status: postmortem.status })}</p>
          {postmortem.status === "processing" && (
            <p>{t("A local Dreamer is processing this report. Refresh to check progress.")}</p>
          )}
          {postmortem.dreamerError && <p>{postmortem.dreamerError}</p>}
          <p>{postmortem.summary}</p>
          {postmortem.purpose && (
            <p>
              {t("Intent: {{key}} · {{toward}}", {
                key: postmortem.purpose.intentKey,
                toward: postmortem.purpose.intent.toward,
              })}
            </p>
          )}
          {(postmortem.dreamerObjectiveFollowups ?? postmortem.objectiveFollowups)?.map(
            (item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: postmortem artifact order is immutable.
              <p key={`${postmortem.artifactId}-followup-${index}`}>{item}</p>
            ),
          )}
          {postmortem.dreamerReview && (
            <article className="whitespace-pre-wrap rounded border border-divider p-3">
              {postmortem.dreamerReview}
            </article>
          )}
        </>
      ) : (
        <>
          <textarea
            aria-label={t("Postmortem summary")}
            placeholder={t("What happened?")}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            className="rounded border border-divider bg-bg p-2"
          />
          <textarea
            aria-label={t("Postmortem outcome")}
            placeholder={t("What was the observed outcome?")}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="rounded border border-divider bg-bg p-2"
          />
          <textarea
            aria-label={t("Postmortem lessons")}
            placeholder={t("One lesson per line")}
            value={lessons}
            onChange={(e) => setLessons(e.target.value)}
            className="rounded border border-divider bg-bg p-2"
          />
          <Button
            disabled={busy || !summary.trim() || !outcome.trim()}
            onClick={() =>
              void act({
                kind: "postmortem",
                input: {
                  runId: run.id,
                  summary,
                  outcome,
                  lessons: lessons
                    .split("\n")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .slice(0, 8),
                },
              })
            }
          >
            {t("Submit to Dreamer inbox")}
          </Button>
        </>
      )}
      {postmortem?.status === "uncertain" && (
        <label className="flex gap-2">
          <input
            type="checkbox"
            checked={acknowledgeUncertain}
            onChange={(e) =>
              setAcknowledgedAttemptId(
                e.target.checked ? (postmortem.dreamerAttemptId ?? null) : null,
              )
            }
          />
          {t("The previous call may have completed. Run another review attempt.")}
        </label>
      )}
      {postmortem && ["queued", "failed", "uncertain"].includes(postmortem.status) && thinkerId && (
        <Button
          disabled={busy || (postmortem.status === "uncertain" && !acknowledgeUncertain)}
          onClick={() =>
            void act({
              kind: "dreamer",
              input: {
                runId: run.id,
                thinkerId,
                effort,
                effortMode,
                retryAttemptId: postmortem.dreamerAttemptId ?? undefined,
                acknowledgeUncertain,
              },
            })
          }
        >
          {postmortem.status === "queued"
            ? t("Process with local Dreamer")
            : t("Retry local Dreamer")}
        </Button>
      )}
      {postmortem?.dreamerReview && run.aiFlowSlug && (
        <ExperimentSection run={run} experiments={experiments} busy={busy} act={act} />
      )}
      {run.aiFlowSlug && (
        <Link
          to="/flows/$slug/edit"
          params={{ slug: run.aiFlowSlug }}
          className="text-accent underline"
        >
          {t("Review flow editor")}
        </Link>
      )}
    </section>
  );
}
